import { useContext, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  fontLandingDisplay,
  radiusLandingControl,
} from "src/design/tokens";
import { LandingButton } from "src/components/LandingPage/components/LandingButton";
import { useScrollToTop, useSectionNavigation } from "./useSectionNavigation";

interface LandingHeaderProps {
  compact?: boolean;
}

const NAV_SECTIONS = [
  { id: "ecosysteme", labelKey: "landingPage.nav.ecosystem" },
  { id: "fonctionnalites", labelKey: "landingPage.nav.features" },
  { id: "analytiques", labelKey: "landingPage.nav.analytics" },
  { id: "tarifs", labelKey: "landingPage.nav.pricing" },
];

/**
 * Sticky landing navigation: display-face wordmark, the four section anchors,
 * the dashboard/login entry and the demo CTA. Language selection lives in the
 * app-wide fixed switcher (LanguageChangeButton), as in the design.
 * `compact` drops everything but the wordmark — used by the standalone public
 * pages (email verification, password reset) that reuse this header.
 */
export const LandingHeader = ({ compact }: LandingHeaderProps) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: userLoading } = useContext(UserContext);

  const scrollToSection = useSectionNavigation();
  const scrollToTop = useScrollToTop();

  const handleNavLink = (id: string) => {
    setDrawerOpen(false);
    scrollToSection(id);
  };

  const goHome = () => {
    if (location.pathname === "/") {
      scrollToTop();
    } else {
      navigate("/");
    }
  };

  const accountButton = (fullWidth?: boolean) =>
    !userLoading && (
      <Button
        fullWidth={fullWidth}
        disableElevation
        onClick={() => {
          setDrawerOpen(false);
          navigate(user ? "/restaurant" : "/login");
        }}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 14,
          borderRadius: radiusLandingControl,
          px: 2.25,
          py: 1,
          color: colorLandingInk,
          border: `1px solid ${colorLandingLine}`,
          "&:hover": {
            borderColor: colorLandingInk,
            background: "transparent",
          },
        }}
      >
        {user ? t("signIn.dashboard") : t("signIn.cta")}
      </Button>
    );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: `${colorLandingSurface}D9`,
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${colorLandingLine}`,
        color: colorLandingInk,
        zIndex: 1200,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{ height: 68, justifyContent: "space-between", gap: 2 }}
        >
          <Box
            onClick={goHome}
            sx={{
              fontFamily: fontLandingDisplay,
              fontSize: { xs: 20, md: 24 },
              lineHeight: 1,
              color: colorLandingInk,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            L'addition
          </Box>

          {!compact && (
            <>
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {NAV_SECTIONS.map((section) => (
                  <Button
                    key={section.id}
                    onClick={() => handleNavLink(section.id)}
                    disableRipple
                    sx={{
                      color: colorLandingMuted,
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: 15,
                      minWidth: 0,
                      px: 0.5,
                      "&:hover": {
                        color: colorLandingInk,
                        background: "transparent",
                      },
                    }}
                  >
                    {t(section.labelKey)}
                  </Button>
                ))}
              </Box>

              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {accountButton()}
                <LandingButton
                  label={t("landingPage.nav.requestDemo")}
                  onClick={() => handleNavLink("demo")}
                  sx={{ px: 2.25, py: 1.15, fontSize: 14 }}
                />
              </Box>

              <IconButton
                aria-label={t("landingPage.nav.openMenu")}
                onClick={() => setDrawerOpen(true)}
                sx={{
                  display: { md: "none" },
                  border: `1px solid ${colorLandingLine}`,
                  borderRadius: "10px",
                  width: 42,
                  height: 42,
                }}
              >
                <MenuIcon sx={{ color: colorLandingInk }} />
              </IconButton>
            </>
          )}
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              onClick={() => {
                setDrawerOpen(false);
                goHome();
              }}
              sx={{
                fontFamily: fontLandingDisplay,
                fontSize: 20,
                cursor: "pointer",
                color: colorLandingInk,
              }}
            >
              L'addition
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <List disablePadding>
            {NAV_SECTIONS.map((section) => (
              <ListItem key={section.id} disablePadding>
                <ListItemButton
                  onClick={() => handleNavLink(section.id)}
                  sx={{ borderRadius: "8px" }}
                >
                  <ListItemText
                    primary={t(section.labelKey)}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 2 }}>{accountButton(true)}</Box>
          <LandingButton
            label={t("landingPage.nav.requestDemo")}
            onClick={() => handleNavLink("demo")}
            fullWidth
            sx={{ mt: 2 }}
          />
        </Box>
      </Drawer>
    </AppBar>
  );
};
